/**
 * Name of the signed-in user's role, for the page header.
 *
 * The profile endpoint returns permissions but not the role they came from, so the role has to be
 * fetched separately. Cached for the session — it does not change while the user is signed in.
 *
 * @module Hooks
 */

import { useQuery } from '@tanstack/react-query';
import { useProfile } from '@/Auth/contexts/ProfileContext';
import { getUserRoles } from '@/Pages/Settings/UserManagement/Services';

/** Roles come back either as plain names or as objects; this flattens both. */
function firstRoleName(roles: (string | { name: string })[]): string | undefined {
  const first = roles[0];
  if (first === undefined) return undefined;
  return typeof first === 'string' ? first : first.name;
}

/** Returns the role name, or `undefined` while loading or when the user has no role. */
export const useCurrentUserRole = (): string | undefined => {
  const { profile } = useProfile();

  const { data } = useQuery({
    queryKey: ['currentUserRole', profile?.id],
    queryFn: () => getUserRoles(profile?.id ?? ''),
    enabled: Boolean(profile?.id),
    staleTime: Infinity,
  });

  return data ? firstRoleName(data.roles) : undefined;
};
